import { ConfigProvider, Popover, Spin } from 'antd';
import Box from 'components/Box';
import IcColor from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal/components/CustomerDetailIcons/IcColor';
import IcGroup from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal/components/CustomerDetailIcons/IcGroup';
import IcHearth from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal/components/CustomerDetailIcons/IcHearth';
import IcLike from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal/components/CustomerDetailIcons/IcLike';
import IcNewCustomer from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal/components/CustomerDetailIcons/IcNewCustomer';
import IcRegularCustomer from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal/components/CustomerDetailIcons/IcRegularCustomer';
import IcVipCustomer from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal/components/CustomerDetailIcons/IcVipCustomer';
import { IAccountPackage } from 'features/customer/services/types/api';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import CustomerProfileOverView from './CustomerProfileOverView';

import Button from 'components/Button';
import { formatPhone, maskPhone } from 'utils/formatPhone';
import ic_info from './ic_info.svg';
import customerSelectors from 'features/customer/services/selectors';
import { useAppDispatch } from 'store/hooks';
import customerActions from 'features/customer/services/actions';
import { verifyPinCode } from 'widgets/RequestAdminLocalPW/Callback';
import shopSelectors from 'services/shop/selectors';
import { useSocketContext } from 'hooks/useSocket';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { IStaffItemData } from 'services/shop/types/staff';
import { STAFF_TYPE } from 'features/cashier/services/types/constant';
import { consentFormRef } from 'features/cashier/components/CenterMainBody/ModalConsentForm';
import { detailsCustomerModalRef } from 'features/customer/pages/ListCustomerPage/components/DetailsCustomerModal';

export const IconInfo = () => <img src={ic_info} alt='info' style={{ width: 32, height: 32 }} />;

export type CustomerInfoPopover = {
  customerId: string;
  customerName: string;
  customerPhone: string;
  accountPackage: IAccountPackage;
  lastTimeVisited: string;
  numberVisited: number;
  customerNote: string;
  customerHobbies: string;
  customerColor: string;
  customerHealth: string;
};

type ICustomerNameProps = {
  customerInfo: CustomerInfoPopover;
  children: React.ReactNode;
  trigger?: 'click' | 'hover';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEdit?: () => void;
};

const renderCustomerAccountPackage = (accountPackage?: IAccountPackage) => {
  switch (accountPackage) {
    case IAccountPackage.NEW:
      return <IcNewCustomer />;
    case IAccountPackage.VIP:
      return <IcVipCustomer />;
    case IAccountPackage.REGULAR:
      return <IcRegularCustomer />;
    default:
      return <IcNewCustomer />;
  }
};
const CustomerName = ({ customerInfo, children, onOpenChange, onEdit }: ICustomerNameProps) => {
  return (
    <div style={{ flex: 1, overflow: 'hidden' }}>
      <ConfigProvider theme={{ components: { Popover: { colorBgElevated: '#E4F4F6' } } }}>
        <Popover
          placement='rightTop'
          title={null}
          content={
            <CustomerInfoPopoverUI
              customerInfo={customerInfo}
              onEdit={onEdit}
              onOpenChange={onOpenChange}
            />
          }
          overlayInnerStyle={{ padding: 16, borderRadius: 8, background: '#E4F4F6' }}
        >
          {children}
        </Popover>
      </ConfigProvider>
    </div>
  );
};

export default CustomerName;

export const CustomerInfoPopoverUI = ({
  customerInfo,
  onEdit,
  onOpenChange,
  loading,
  dataPin,
}: {
  customerInfo?: CustomerInfoPopover;
  onEdit?: () => void;
  onOpenChange?: (open: boolean) => void;
  loading?: boolean;
  dataPin?: IStaffItemData;
}) => {
  const setLoading = useSetLoadingPage();
  const socketContext = useSocketContext();
  const isAdmin = dataPin?.staffType === STAFF_TYPE.ADMIN || dataPin?.staffType === STAFF_TYPE.MANAGER;

  if (loading) {
    return (
      <ContentPopover style={{ height: 100 }}>
        <Box>
          <Spin spinning />
        </Box>
      </ContentPopover>
    );
  }

  return (
    <ContentPopover>
      <Header>
        <Box display='flex' alignItems='center' style={{ gap: 8 }}>
          {renderCustomerAccountPackage(customerInfo?.accountPackage)}
          <Name>{customerInfo?.customerName}</Name>
        </Box>
        <Phone>{!isAdmin ? maskPhone(customerInfo?.customerPhone || '') : formatPhone(customerInfo?.customerPhone || '')}</Phone>
        <Box display='flex'>
          <DetailInfo>
            <span className='label'>Last visited date:</span>
            <span className='value'>{customerInfo?.lastTimeVisited}</span>
          </DetailInfo>
          <Divider />
          <DetailInfo>
            <span className='label'>Visited:</span>
            <span className='value'>{customerInfo?.numberVisited || 0}</span>
          </DetailInfo>
        </Box>
      </Header>
      <AdvertiseBoxStyled>
        {customerInfo?.customerNote && (
          <CustomerProfileOverView
            icon={<IcGroup fill='#2D6772' />}
            label='Note'
            value={customerInfo?.customerNote || '--'}
          />
        )}
        {customerInfo?.customerHobbies && (
          <CustomerProfileOverView
            icon={<IcLike fill='#2D6772' />}
            label='Hobbies'
            value={customerInfo?.customerHobbies || '--'}
          />
        )}
        {customerInfo?.customerHealth && (
          <CustomerProfileOverView
            icon={<IcHearth fill='#2D6772' />}
            label='Health Status'
            value={customerInfo?.customerHealth || '--'}
          />
        )}
        {customerInfo?.customerColor && (
          <CustomerProfileOverView
            icon={<IcColor fill='#2D6772' />}
            label='Nail polish'
            value={customerInfo?.customerColor || '--'}
          />
        )}
      </AdvertiseBoxStyled>
      {onEdit && (
        <FooterStyled>
          <Button
            block
            cardCashier
            ntype='SECONDARY'
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange?.(false);
            }}
          >
            <span style={{ fontSize: 18 }}>Close</span>
          </Button>
          <Button block cardCashier ntype='LIGHT_BLUE' onClick={async (e) => {
            e.stopPropagation();
            try {
              await consentFormRef.current?.openNonConfirm(customerInfo?.customerId || '');
            } catch (error) { }
          }}><span style={{ fontSize: 18 }}>Consent Form</span></Button>
          <Button
            block
            cardCashier
            ntype='PRIMARY'
            disabled={!isAdmin}
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
          >
            <span style={{ fontSize: 18, color: '#fff' }}>Edit</span>
          </Button>
          <Button block cardCashier ntype='WHITE_BLUE' onClick={async (e) => {
            e.stopPropagation();
            onOpenChange?.(false);
            detailsCustomerModalRef.current?.open(customerInfo?.customerId || '');
          }}><span style={{ fontSize: 18 }}>View Detail</span></Button>
        </FooterStyled>
      )}
    </ContentPopover>
  );
};

export const useCustomerInfoPopover = (customerId: string) => {
  const dispatch = useAppDispatch();
  const customerDetailLoading = customerSelectors.popoverCustomerDetailLoading();
  const dataCustomer = customerSelectors.popoverCustomerDetail();
  const [open, setOpen] = useState(false);
  const [dataPin, setDataPin] = useState<IStaffItemData | undefined>();
  const allSetting = shopSelectors.data.allSetting();
  const customerInfo: CustomerInfoPopover = useMemo(() => {
    return {
      customerId: dataCustomer?.id || '',
      customerName: dataCustomer?.name || '',
      customerPhone: dataCustomer?.phone || '',
      customerNote: dataCustomer?.note || '',
      customerHobbies: dataCustomer?.hobbies || '',
      customerColor: dataCustomer?.color || '',
      customerHealth: dataCustomer?.health || '',
      lastTimeVisited: dataCustomer?.lastVisitedDate || '',
      numberVisited: dataCustomer?.numberVisit || 0,
      accountPackage: dataCustomer?.accountPackage || IAccountPackage.NEW,
    };
  }, [dataCustomer]);
  const callback = (pin?: string, data?: IStaffItemData) => {
    setDataPin(data);
    setOpen(true);
    dispatch(customerActions.viewCustomerDetailById.fetch(customerId));
  };
  const viewCustomer = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!customerId) return;
    e.stopPropagation();
    if (allSetting?.verifyPassInformationCustomer) return verifyPinCode(callback, true);
    callback();
  };

  const handleCheckPassBeforeView = (val: boolean) => {
    if (!val) setOpen(false);
  };

  return {
    customerInfo,
    customerDetailLoading,
    viewCustomer,
    open,
    setOpen,
    handleCheckPassBeforeView,
    dataPin,
    callback,
  };
};

const ContentPopover = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 580px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Name = styled.div`
  color: #1d2129;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 100% */
  text-transform: uppercase;
`;

const Phone = styled.div`
  color: #1d2129;
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 100% */
`;
const Divider = styled.div`
  width: 1px;
  // height: 18px;
  background: #bfcad6;
  margin: 0 24px;
`;

const DetailInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2d6772;
  .label {
    font-size: 20px;
    font-weight: 500;
    line-height: normal;
  }
  .value {
    font-size: 22px;
    font-weight: 600;
    line-height: 30px;
  }
`;

const AdvertiseBoxStyled = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background: #fff;
`;

const FooterStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 16px 24px 16px;
  margin: 16px -16px -16px -16px;
  background: #fff;
  box-shadow: 0px -4px 8px 0px rgba(0, 0, 0, 0.15);
`;
